// src/components/serials/tags/artEnrich.js
import { getArtLabelGeometry } from "../../artLabels/functions";
import { DEFAULT_GEOMETRY } from "../../artLabels/artLabelPreviewMath";
import { platesFromImageUrl, DEFAULT_PLATES } from "./artPlates";
import { isArtLabelsEnabled } from "./artLabelsFlag";
import { isExtendedArt } from "../../extendedArt/kind";

const needsPlates = (card) => isExtendedArt(card) && Boolean(card.art?.url);

/**
 * Placas de contraste e geometria da etiqueta para os cartões de arte
 * estendida. A arte já vem do serial (`serial.artLabel.approved`, D4); seriais
 * comuns nunca recebem arte (spec 2026-09-30 §5.4) e passam intactos.
 * Placas: uma amostragem do PNG por `artKey@version`. O `cache` (Map) pode
 * viver entre chamadas: a URL presign expira em 600 s e um reenriquecimento
 * tardio (ex.: depois do save) não consegue mais baixar a imagem; com o cache,
 * as placas já amostradas são reaproveitadas. Falha não fica no cache.
 */
export async function enrichCardsWithArt({
  cards, token,
  fetchGeometry = getArtLabelGeometry, plates = platesFromImageUrl,
  enabled = isArtLabelsEnabled(),
  cache = new Map(),
}) {
  if (!enabled || !(cards || []).some(needsPlates)) return { cards, geometry: DEFAULT_GEOMETRY };

  let geometry = DEFAULT_GEOMETRY;
  try {
    const g = await fetchGeometry(token);
    if (g?.type === "success" && g.data) geometry = { ...DEFAULT_GEOMETRY, ...g.data };
  } catch (_err) { /* fallback */ }

  // Cada entrada resolve com as placas ou `null` (falha). A falha sai do cache
  // para a próxima chamada tentar de novo; quem já esperava recebe o padrão.
  const platesFor = (art) => {
    const key = `${art.artKey}@${art.version}`;
    if (!cache.has(key)) {
      const entry = Promise.resolve()
        .then(() => plates(art.url, geometry))
        .catch(() => {
          if (cache.get(key) === entry) cache.delete(key);
          return null;
        });
      cache.set(key, entry);
    }
    return cache.get(key);
  };

  const enriched = await Promise.all(cards.map(async (card) => {
    if (!needsPlates(card)) return card;
    const resolved = (await platesFor(card.art)) || { ...DEFAULT_PLATES };
    return { ...card, art: { ...card.art, plates: resolved } };
  }));
  return { cards: enriched, geometry };
}
